import {
    PiCheckCircleBold,
    PiClockBold,
    PiFileBold,
    PiFilePdfBold,
    PiImageBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import type { PloomesFileItem } from './types';

type Props = {
    file: PloomesFileItem;
    onClick: () => void;
};

export default function FileGridCard({ file, onClick }: Props) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'group flex flex-col overflow-hidden rounded-xl border text-left shadow-sm transition',
                'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md',
                'dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600',
            )}
        >
            {/* Thumbnail */}
            <div className="relative flex h-36 items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/60 dark:to-gray-800/80">
                {file.is_image && file.access_url ? (
                    <img
                        src={file.access_url}
                        alt=""
                        className="size-full object-cover transition group-hover:scale-[1.02]"
                        loading="lazy"
                    />
                ) : (
                    <FileTypeIcon file={file} large />
                )}
                <span className="absolute right-2 top-2">
                    {file.is_validated ? (
                        <span className="inline-flex size-7 items-center justify-center rounded-full bg-emerald-500 text-white shadow">
                            <PiCheckCircleBold className="size-4" />
                        </span>
                    ) : (
                        <span className="inline-flex size-7 items-center justify-center rounded-full bg-amber-400 text-white shadow">
                            <PiClockBold className="size-4" />
                        </span>
                    )}
                </span>
            </div>

            {/* Info */}
            <div className="flex flex-1 flex-col gap-1 p-3">
                <p className="line-clamp-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {file.display_name}
                </p>
                {file.category_label && (
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{file.category_label}</p>
                )}
                <div className="mt-auto flex items-center justify-between pt-2 text-[11px] text-gray-400 dark:text-gray-500">
                    <span>{file.readable_size ?? '—'}</span>
                    <span>{formatDate(file.ploomes_created_at)}</span>
                </div>
            </div>
        </button>
    );
}

export function FileTypeIcon({ file, large }: { file: PloomesFileItem; large?: boolean }) {
    const cls = large ? 'size-12' : 'size-5';
    if (file.is_pdf)   return <PiFilePdfBold className={cn(cls, 'text-rose-500')} />;
    if (file.is_image) return <PiImageBold className={cn(cls, 'text-sky-500')} />;
    return <PiFileBold className={cn(cls, 'text-gray-400 dark:text-gray-500')} />;
}

function formatDate(iso: string | null) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}
